import React from 'react';
import { cn } from '@shared/ui/utils/tailwind';

type TextProps = {
  variant?: string;
  size?: string;
  fw?: string | number;
  mt?: string | number;
  mb?: string | number;
  c?: string;
  ta?: string;
  tt?: string;
  fs?: string;
  as?: React.ElementType;
  component?: React.ElementType;
  span?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  [key: string]: any;
};

const variantClasses: Record<string, string> = {
  title: 'text-lg font-semibold',
  body: 'text-base',
  caption: 'text-sm text-[var(--text-secondary)]',
  label: 'text-sm font-medium',
  empty: '',
};

const sizeClasses: Record<string, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
};

const colorMap: Record<string, string> = {
  dimmed: 'var(--text-secondary)',
};

function resolveSpacing(value: string | number): string {
  return typeof value === 'number' ? `${value}px` : value;
}

function resolveColor(value: string): string {
  return colorMap[value] ?? value;
}

type StyleEntry = [string, unknown];

function buildComputedStyle(
  entries: StyleEntry[],
  baseStyle?: React.CSSProperties,
): React.CSSProperties | undefined {
  const result: Record<string, unknown> = {};
  for (const [key, value] of entries) {
    if (value != null) {
      result[key] = value;
    }
  }
  Object.assign(result, baseStyle);
  return Object.keys(result).length > 0 ? (result as React.CSSProperties) : undefined;
}

function resolveTag(component?: React.ElementType, as?: React.ElementType, span?: boolean): React.ElementType {
  return component || as || (span ? 'span' : 'p');
}

function resolveMargin(value: string | number | undefined): string | null {
  if (value == null) return null;
  return resolveSpacing(value);
}

function resolveVariantClass(variant: string): string {
  return variantClasses[variant] || '';
}

function resolveSizeClass(size: string | undefined): string {
  return sizeClasses[size ?? ''] || '';
}

function resolveColorValue(c: string | undefined): string | null {
  return c ? resolveColor(c) : null;
}

const Text: React.FC<TextProps> = ({
  variant = 'empty',
  size,
  fw,
  mt,
  mb,
  c,
  ta,
  tt,
  fs: fontStyle,
  as,
  component,
  span,
  className,
  style,
  children,
  ...props
}) => {
  const Tag = resolveTag(component, as, span);

  const entries: StyleEntry[] = [
    ['fontWeight', fw],
    ['marginTop', resolveMargin(mt)],
    ['marginBottom', resolveMargin(mb)],
    ['color', resolveColorValue(c)],
    ['textAlign', ta],
    ['textTransform', tt],
    ['fontStyle', fontStyle],
  ];

  return (
    <Tag
      className={cn('whitespace-pre-wrap text-[var(--text-primary)] m-0', resolveVariantClass(variant), resolveSizeClass(size), className)}
      style={buildComputedStyle(entries, style)}
      {...props}
    >
      {children}
    </Tag>
  );
};

export default Text;
